import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { FormControl, useTheme } from "@mui/material";
import { Info, Menu as MenuIcon, NotificationsOutlined } from "@mui/icons-material";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { useState } from "react";
import AssetSelector from "../../Selects/AssetSelector/AssetSelector";
import { useNotificationsOptional } from "../../../context/NotificationsContext";
import CategorizedSelect from "../../Selects/CategorizedSelect";
import HelpIcon from "@mui/icons-material/Help";
import ModalDialog from "../../Modals/ModalDialog/ModalDialog";
import SimpleTextField from "../../Forms/FieldTypes/TextField";
import UserProfile from "../../../pages/users/UserProfile";

export default function TopNav(props) {
  const {
    apiUrl,
    disableTextBox = false,
    noOptionsText,
    onItemClick = () => { },
    onSearchBarChange = () => { },
    onSearchBarClose = () => { },
    searchResults = [],
    searchText = {},
    token,
    useAssetSearch = true,
    useTextBox = false,
    useCategorizedSelect = false,
  } = props;
  const theme = useTheme();
  const history = useHistory();
  const { palette = {} } = theme;
  const { common = {}, primary = {}, success = {}, background = {}, text = {}, divider, mode } = palette;
  const isDark = mode === "dark";
  
  // Brand colors
  const navyColor = primary?.main || "#002843";
  const greenColor = success?.main || "#A0C46D";
  const greenDark = "#7a9c4a";
  const bgPaper = background?.paper || (isDark ? "#032d42" : "#ffffff");
  const textPrimary = text?.primary || (isDark ? "#ffffff" : "#1e293b");
  const textSecondary = text?.secondary || (isDark ? "#94a3b8" : "#64748b");
  const borderColor = divider || (isDark ? "rgba(148, 163, 184, 0.2)" : "#e2e8f0");
  
  const classes = {
    appBar: {
      backgroundColor: bgPaper,
      boxShadow: isDark ? "0 2px 8px rgba(0, 0, 0, 0.2)" : "0 2px 8px rgba(0, 0, 0, 0.06)",
      borderBottom: `1px solid ${borderColor}`,
      paddingLeft: "95px",
    },
    avatar: {
      backgroundColor: greenColor,
      color: "#ffffff",
      fontWeight: 600,
      fontSize: "0.8rem",
      width: 38,
      height: 38,
      boxShadow: "0 2px 8px rgba(160, 196, 109, 0.4)",
      transition: "all 0.2s ease-in-out",
      border: `2px solid ${bgPaper}`,
      "&:hover": {
        transform: "scale(1.05)",
        boxShadow: "0 4px 16px rgba(160, 196, 109, 0.5)",
      },
    },
    dropdownAvatar: {
      backgroundColor: greenColor,
      color: "#ffffff",
    },
    iconButton: {
      backgroundColor: isDark ? "rgba(148, 163, 184, 0.08)" : "#f8fafc",
      borderRadius: "10px",
      padding: "10px",
      marginLeft: "8px",
      border: `1px solid ${borderColor}`,
      color: textSecondary,
      transition: "all 0.2s ease-in-out",
      "&:hover": {
        backgroundColor: isDark ? "rgba(160, 196, 109, 0.15)" : "rgba(160, 196, 109, 0.1)",
        borderColor: greenColor,
        color: isDark ? greenColor : greenDark,
        transform: "translateY(-1px)",
        boxShadow: "0 4px 12px rgba(160, 196, 109, 0.2)",
      },
      "&:focus-visible": {
        outline: "none",
        boxShadow: `0 0 0 2px ${greenColor}40`,
      },
    },
    versionBadge: {
      background: isDark ? "rgba(148, 163, 184, 0.1)" : "#f1f5f9",
      color: isDark ? greenColor : navyColor,
      padding: "6px 14px",
      borderRadius: "20px",
      fontSize: "0.7rem",
      fontWeight: 600,
      letterSpacing: "0.5px",
      marginRight: "12px",
      border: `1px solid ${borderColor}`,
    },
    menuPaper: {
      borderRadius: "12px",
      boxShadow: isDark ? "0 10px 40px rgba(0, 0, 0, 0.4)" : "0 10px 40px rgba(0, 0, 0, 0.12)",
      border: `1px solid ${borderColor}`,
      overflow: "hidden",
      marginTop: "8px",
      backgroundColor: bgPaper,
    },
    menuItem: {
      padding: "12px 20px",
      fontSize: "0.875rem",
      color: textPrimary,
      transition: "all 0.15s ease-in-out",
      "&:hover": {
        backgroundColor: isDark ? "rgba(160, 196, 109, 0.15)" : "rgba(160, 196, 109, 0.1)",
        color: isDark ? greenColor : greenDark,
      },
    },
  };

  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [anchorElInfo, setAnchorElInfo] = useState(null);
  const [anchorElNotifications, setAnchorElNotifications] = useState(null);
  const [moreEl, setMoreEl] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const notificationsContext = useNotificationsOptional();
  const unreadCount = notificationsContext?.unreadCount ?? 0;
  const recentNotifications = (notificationsContext?.notifications ?? []).slice(0, 5);
  const hasNotifications = unreadCount > 0;

  const handleNavMenuOpen = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleUserMenuOpen = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleInfoMenuOpen = (event) => {
    setAnchorElInfo(event.currentTarget);
  };

  const handleMoreMenuOpen = (event) => {
    setMoreEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorElNav(null);
    setAnchorElInfo(null);
    setAnchorElUser(null);
    setAnchorElNotifications(null);
    setMoreEl(null);
  };

  const handleNotificationsOpen = (event) => {
    setAnchorElNotifications(event.currentTarget);
    notificationsContext?.markAsSeen?.();
  };

  const navLinks = [];

  const firstInitial = localStorage
    .getItem("firstName")
    ?.charAt(0)
    .toUpperCase();
  const lastInitial = localStorage.getItem("lastName")?.charAt(0).toUpperCase();
  const initials =
    firstInitial && lastInitial ? `${firstInitial}${lastInitial}` : null;

  const email = localStorage.getItem("userEmail");

  const versionNumber = "v5.0.0";

  return (
    <Box>
      <ModalDialog
        open={userModalOpen}
        handleClose={() => setUserModalOpen(false)}
        title="User Profile"
        content={<UserProfile apiUrl={apiUrl} token={token} />}
      />
      <AppBar sx={classes.appBar} elevation={0}>
        <Toolbar>
          {/* Left Section: Navigation Links */}
          <Box
            sx={{
              alignItems: "center",
              display: { xs: "none", md: "flex" },
              flexGrow: 1,
            }}
          >
            {navLinks.map((link) => (
              <Button key={link} color="inherit" onClick={handleNavMenuOpen}>
                {link}
              </Button>
            ))}
            {/* Dropdown for "More" */}
            {navLinks.length > 0 && (
              <>
                <Button color="inherit" onClick={handleMoreMenuOpen}>
                  More
                </Button>
                <Menu
                  anchorEl={moreEl}
                  open={Boolean(moreEl)}
                  onClose={handleClose}
                  PaperProps={{ sx: classes.menuPaper }}
                >
                  <MenuItem onClick={handleClose} sx={classes.menuItem}>More Link 1</MenuItem>
                  <MenuItem onClick={handleClose} sx={classes.menuItem}>More Link 2</MenuItem>
                </Menu>
              </>
            )}
          </Box>

          {/* Center Section: Search Field */}
          <Box
            sx={{
              flexGrow: 1,
              display: { xs: "flex", md: "flex" },
              justifyContent: "center",
              alignItems: "center",
              marginRight: { xs: 2, md: 0 },
              marginLeft: { xs: 2, md: 0 },
            }}
          >
            {useAssetSearch ? <AssetSelector {...props} /> : null}
            {useTextBox ? (
              <FormControl sx={{ width: "100%" }} variant="filled">
                <SimpleTextField
                  disabled={disableTextBox}
                  fullWidth
                  externalLabel={false}
                  label="Search for an asset..."
                  onChange={onSearchBarChange}
                  sx={{
                    backgroundColor: bgPaper,
                    color: textPrimary,
                  }}
                  variant="filled"
                  value={searchText}
                />
              </FormControl>
            ) : null}
            {useCategorizedSelect ? (
              <FormControl sx={{ width: "100%" }} variant="filled">
                <CategorizedSelect
                  assets={searchResults}
                  noOptionsText={noOptionsText}
                  onClose={onSearchBarClose}
                  onItemClick={onItemClick}
                  value={searchText}
                  onChange={onSearchBarChange}
                />
              </FormControl>
            ) : null}
          </Box>

          {/* Right Section: Icons */}
          <Box
            sx={{
              alignItems: "center",
              flexGrow: 1,
              display: { xs: "none", md: "flex" },
              justifyContent: "flex-end",
              gap: "4px",
            }}
          >
            <Box sx={classes.versionBadge}>
              {versionNumber}
            </Box>

            {/* Notification indicator – badge when there are unread notifications */}
            {notificationsContext != null && (
              <>
                <Tooltip title={hasNotifications ? `${unreadCount} new notification${unreadCount !== 1 ? "s" : ""}` : "Notifications"} placement="bottom" arrow>
                  <IconButton
                    onClick={handleNotificationsOpen}
                    sx={{
                      ...classes.iconButton,
                      position: "relative",
                    }}
                    aria-label={hasNotifications ? `${unreadCount} unread notifications` : "Notifications"}
                  >
                    <Badge
                      variant="dot"
                      invisible={!hasNotifications}
                      sx={{
                        "& .MuiBadge-badge": {
                          backgroundColor: "#ef4444",
                          color: "#fff",
                          border: `2px solid ${bgPaper}`,
                          minWidth: "10px",
                          height: "10px",
                          right: 2,
                          top: 2,
                        },
                      }}
                    >
                      <NotificationsOutlined sx={{ fontSize: "1.2rem" }} />
                    </Badge>
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={anchorElNotifications}
                  open={Boolean(anchorElNotifications)}
                  onClose={handleClose}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  PaperProps={{
                    sx: {
                      ...classes.menuPaper,
                      minWidth: "320px",
                      maxWidth: "380px",
                      maxHeight: "400px",
                    },
                  }}
                >
                  <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${borderColor}` }}>
                    <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem", color: textPrimary }}>
                      Notifications
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: textSecondary, mt: 0.25 }}>
                      Event-driven alerts from your assets
                    </Typography>
                  </Box>
                  <Box sx={{ maxHeight: "280px", overflowY: "auto" }}>
                    {recentNotifications.length === 0 ? (
                      <Typography sx={{ px: 2, py: 2, fontSize: "0.8125rem", color: textSecondary }}>
                        No recent notifications
                      </Typography>
                    ) : (
                      recentNotifications.map((n) => (
                        <MenuItem
                          key={n.id}
                          component={RouterLink}
                          to="/"
                          onClick={handleClose}
                          sx={{
                            ...classes.menuItem,
                            flexDirection: "column",
                            alignItems: "flex-start",
                            whiteSpace: "normal",
                          }}
                        >
                          <Typography sx={{ fontWeight: 600, fontSize: "0.8125rem" }}>{n.tag}</Typography>
                          <Typography sx={{ fontSize: "0.75rem", color: textSecondary }}>{n.event}</Typography>
                          {n.facility && (
                            <Typography sx={{ fontSize: "0.7rem", color: textSecondary }}>📍 {n.facility}</Typography>
                          )}
                        </MenuItem>
                      ))
                    )}
                  </Box>
                  <MenuItem
                    component={RouterLink}
                    to="/"
                    onClick={handleClose}
                    sx={{
                      ...classes.menuItem,
                      justifyContent: "center",
                      borderTop: `1px solid ${borderColor}`,
                      backgroundColor: isDark ? "rgba(160, 196, 109, 0.08)" : "rgba(160, 196, 109, 0.06)",
                    }}
                  >
                    View all on Dashboard
                  </MenuItem>
                </Menu>
              </>
            )}

            <Tooltip title="Support" placement="bottom" arrow>
              <IconButton sx={classes.iconButton}>
                <Link
                  href="http://helpdesk.locatorx.com/support/home"
                  rel="noopener noreferrer"
                  sx={{
                    color: "inherit",
                    "&:hover": {
                      color: "inherit",
                    },
                    textDecoration: "none",
                    display: "flex",
                    alignItems: "center",
                  }}
                  target="_blank"
                >
                  <HelpIcon sx={{ fontSize: "1.2rem" }} />
                </Link>
              </IconButton>
            </Tooltip>

            {/* Info Icon (has menu) */}
            <Tooltip title="Information" placement="bottom" arrow>
              <IconButton sx={classes.iconButton} onClick={handleInfoMenuOpen}>
                <Info sx={{ fontSize: "1.2rem" }} />
              </IconButton>
            </Tooltip>

            {/* User Avatar/Profile (has menu) */}
            <Tooltip title={email} placement="bottom" arrow>
              <IconButton
                onClick={handleUserMenuOpen}
                sx={{
                  ml: 1.5,
                  p: 0,
                  "&:focus-visible": {
                    outline: "none",
                    boxShadow: `0 0 0 2px ${greenColor}40`,
                  },
                }}
              >
                <Avatar sx={classes.avatar}>{initials}</Avatar>
              </IconButton>
            </Tooltip>

            {/* Menu for Information */}
            <Menu
              anchorEl={anchorElInfo}
              open={Boolean(anchorElInfo)}
              onClose={handleClose}
              PaperProps={{ sx: classes.menuPaper }}
            >
              {/* privacy policy link */}
              <Link
                href="https://wiki.locatorx.com/privacy-policy/"
                rel="noopener noreferrer"
                sx={{ textDecoration: "none", color: "inherit" }}
                target="_blank"
              >
                <MenuItem sx={classes.menuItem}>Privacy Policy</MenuItem>
              </Link>
              {/* terms of use link */}
              <Link
                href="https://wiki.locatorx.com/terms-of-use/"
                rel="noopener noreferrer"
                sx={{ textDecoration: "none", color: "inherit" }}
                target="_blank"
              >
                <MenuItem sx={classes.menuItem}>Terms of Use</MenuItem>
              </Link>
            </Menu>

            {/* Menu for User Profile */}
            <Menu
              anchorEl={anchorElUser}
              open={Boolean(anchorElUser)}
              onClose={handleClose}
              PaperProps={{ sx: classes.menuPaper }}
            >
              <MenuItem onClick={() => setUserModalOpen(true)} sx={classes.menuItem}>
                Profile
              </MenuItem>
              <RouterLink
                to={{ pathname: "/logout" }}
                style={{
                  textDecoration: "none",
                  color: "inherit",
                }}
              >
                <MenuItem onClick={handleClose} sx={classes.menuItem}>Logout</MenuItem>
              </RouterLink>
            </Menu>
          </Box>

          {/* Responsive Hamburger Menu */}
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
            }}
          >
            <IconButton
              sx={{ color: navyColor }}
              edge="start"
              onClick={handleNavMenuOpen}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={anchorElNav}
              open={Boolean(anchorElNav)}
              onClose={handleClose}
              PaperProps={{ sx: classes.menuPaper }}
              sx={{
                alighItems: "center",
                justifyContent: "center",
              }}
            >
              {navLinks.length > 0 && (
                <>
                  {navLinks.map((link) => (
                    <MenuItem key={link} onClick={handleClose} sx={classes.menuItem}>
                      {link}
                    </MenuItem>
                  ))}
                  <MenuItem onClick={handleClose} sx={classes.menuItem}>More</MenuItem>
                </>
              )}

              {notificationsContext != null && (
                <MenuItem
                  onClick={() => {
                    history.push("/");
                    handleClose();
                    notificationsContext?.markAsSeen?.();
                  }}
                  sx={classes.menuItem}
                >
                  Notifications {hasNotifications ? `(${unreadCount})` : ""}
                </MenuItem>
              )}
              <MenuItem onClick={() => setUserModalOpen(true)} sx={classes.menuItem}>
                Profile
              </MenuItem>

              <RouterLink
                to={{ pathname: "/logout" }}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <MenuItem onClick={handleClose} sx={classes.menuItem}>Logout</MenuItem>
              </RouterLink>
              <Link
                href="http://helpdesk.locatorx.com/support/home"
                rel="noopener noreferrer"
                style={{ textDecoration: "none", color: "inherit" }}
                target="_blank"
              >
                <MenuItem sx={classes.menuItem}>Support</MenuItem>
              </Link>
              <MenuItem divider={true} onClick={handleInfoMenuOpen} sx={classes.menuItem}>
                Information
              </MenuItem>

              <MenuItem
                sx={{
                  ...classes.menuItem,
                  cursor: "default",
                  justifyContent: "center",
                  textDecoration: "none",
                  fontWeight: 600,
                  color: navyColor,
                }}
              >
                {versionNumber}
              </MenuItem>
              <MenuItem
                sx={{
                  ...classes.menuItem,
                  cursor: "default",
                  justifyContent: "center",
                  textDecoration: "none",
                }}
              >
                <Tooltip title={email} placement="left">
                  <Avatar sx={classes.dropdownAvatar}>{initials}</Avatar>
                </Tooltip>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
    </Box>
  );
}
